import React, { useState, useEffect } from 'react';
import { Box, Typography, Grid, Paper, Chip, Skeleton, Snackbar, Alert, TextField, FormControl, InputLabel, Select, MenuItem, Button, TableContainer, Table, TableHead, TableRow, TableCell, TableBody, TablePagination, Menu } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import ReportService from '../../../api/services/reportService';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import ReplayIcon from '@mui/icons-material/Replay';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import { formatCurrency } from '../../../utils/currency';
import { standardFilterInputSx, standardFilterSelectSx, standardResetButtonSx, standardSecondaryButtonSx } from '../../../components/common/tableStyles';

export default function ReservationReportTab({ tenantId, roomTypes, isActive }) {
    const [resData, setResData] = useState([]);
    const [resSummary, setResSummary] = useState(null);
    const [isLoadingRes, setIsLoadingRes] = useState(false);
    const [resError, setResError] = useState(null);
    const [resFilters, setResFilters] = useState({
        startDate: '',
        endDate: '',
        period: 'daily',
        status: '',
        bookingSource: '',
        roomTypeId: ''
    });
    const [resPage, setResPage] = useState(0);
    const [resRowsPerPage, setResRowsPerPage] = useState(20);
    const [exportAnchorEl, setExportAnchorEl] = useState(null);
    const [isExporting, setIsExporting] = useState(false);

    useEffect(() => {
        if (isActive) {
            fetchResData();
        }
    }, [isActive, tenantId, resFilters, resPage, resRowsPerPage]);

    const fetchResData = async (isExport = false) => {
        setIsLoadingRes(true);
        setResError(null);
        try {
            const params = { ...resFilters };
            Object.keys(params).forEach(key => {
                if (params[key] === '' || params[key] === null || params[key] === undefined) delete params[key];
            });
            
            if (isExport) {
                params.noPaging = true;
            } else {
                params.page = resPage + 1;
                params.limit = resRowsPerPage;
            }
            
            const res = await ReportService.getReservationReport(tenantId, params);
            const reportData = res?.data || res;
            setResSummary(reportData?.summary || null);
            setResData(reportData?.reservations || reportData?.items || []);
        } catch (error) {
            console.error('Error fetching reservation report:', error);
            setResError('Failed to load reservation report. Please try again.');
        } finally {
            setIsLoadingRes(false);
        }
    };

    const handleResFilterChange = (field, value) => {
        setResFilters(prev => ({ ...prev, [field]: value }));
        setResPage(0);
    };

    const resetResFilters = () => {
        setResFilters({
            startDate: '',
            endDate: '',
            period: 'daily',
            status: '',
            bookingSource: '',
            roomTypeId: ''
        });
        setResPage(0);
    };

    const handleExportClick = (event) => {
        setExportAnchorEl(event.currentTarget);
    };

    const handleExportClose = () => {
        setExportAnchorEl(null);
    };

    const handleExport = async (format) => {
        handleExportClose();
        setIsExporting(true);
        setResError(null);
        try {
            // Force the API to return CSV, which we will convert in the browser
            const params = { ...resFilters, format: 'csv' };
            Object.keys(params).forEach(key => {
                if (params[key] === '' || params[key] === null || params[key] === undefined) delete params[key];
            });
            const res = await ReportService.exportReservationReport(tenantId, params);
            
            // Extract the raw CSV text safely
            let csvText = '';
            if (res.data instanceof Blob) {
                csvText = await res.data.text();
            } else if (typeof res.data === 'string') {
                csvText = res.data;
            } else {
                csvText = JSON.stringify(res.data);
            }
            
            // Parse CSV into an array of arrays
            const rows = csvText.split('\n')
                .map(row => row.split(',').map(cell => cell.replace(/"/g, '').trim()))
                .filter(row => row.length > 0 && row.some(cell => cell !== ''));
                
            const fileName = `Reservations_Report_${new Date().toISOString().split('T')[0]}`;
            
            if (format === 'csv') {
                const blob = new Blob([csvText], { type: 'text/csv;charset=utf-8;' });
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `${fileName}.csv`;
                document.body.appendChild(a);
                a.click();
                a.remove();
                window.URL.revokeObjectURL(url);
            } else if (format === 'excel') {
                const ws = XLSX.utils.aoa_to_sheet(rows);
                const wb = XLSX.utils.book_new();
                XLSX.utils.book_append_sheet(wb, ws, "Reservations");
                XLSX.writeFile(wb, `${fileName}.xlsx`);
            } else if (format === 'pdf') {
                const doc = new jsPDF();
                const headers = rows[0] || [];
                const body = rows.slice(1) || [];
                
                doc.text("Reservations Report", 14, 15);
                autoTable(doc, {
                    head: [headers],
                    body: body,
                    startY: 20,
                    styles: { fontSize: 8, cellPadding: 2 },
                    headStyles: { fillColor: [63, 81, 181] }
                });
                doc.save(`${fileName}.pdf`);
            }
        } catch (error) {
            console.error(`Error exporting reservations report as ${format}:`, error);
            setResError(`Failed to export ${format.toUpperCase()} report. Please try again.`);
        } finally {
            setIsExporting(false);
        }
    };

    return (
        <Box>
            <Snackbar 
                open={!!resError} 
                autoHideDuration={6000} 
                onClose={() => setResError(null)}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert severity="warning" onClose={() => setResError(null)}>{resError}</Alert>
            </Snackbar>

            <Paper sx={{ p: 2, mb: 3, borderRadius: '12px', border: '1px solid #edf1f6', bgcolor: '#ffffff', boxShadow: 'none' }}>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={6} md={2}>
                        <TextField
                            label="Start Date"
                            type="date"
                            size="small"
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                            value={resFilters.startDate}
                            onChange={(e) => handleResFilterChange('startDate', e.target.value)}
                            sx={standardFilterInputSx}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={2}>
                        <TextField
                            label="End Date"
                            type="date"
                            size="small"
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                            value={resFilters.endDate}
                            onChange={(e) => handleResFilterChange('endDate', e.target.value)}
                            sx={standardFilterInputSx}
                        />
                    </Grid>
                    <Grid item xs={12} sm={4} md={2}>
                        <FormControl size="small" fullWidth>
                            <Select
                                value={resFilters.period}
                                displayEmpty
                                onChange={(e) => handleResFilterChange('period', e.target.value)}
                                sx={standardFilterSelectSx}
                            >
                                <MenuItem value="daily">Daily</MenuItem>
                                <MenuItem value="weekly">Weekly</MenuItem>
                                <MenuItem value="monthly">Monthly</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={4} md={2}>
                        <FormControl size="small" fullWidth>
                            <Select
                                value={resFilters.status}
                                displayEmpty
                                onChange={(e) => handleResFilterChange('status', e.target.value)}
                                sx={standardFilterSelectSx}
                            >
                                <MenuItem value="">All Statuses</MenuItem>
                                <MenuItem value="Confirmed">Confirmed</MenuItem>
                                <MenuItem value="CheckedIn">Checked In</MenuItem>
                                <MenuItem value="CheckedOut">Checked Out</MenuItem>
                                <MenuItem value="Cancelled">Cancelled</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={4} md={2}>
                        <FormControl size="small" fullWidth>
                            <Select
                                value={resFilters.bookingSource}
                                displayEmpty
                                onChange={(e) => handleResFilterChange('bookingSource', e.target.value)}
                                sx={standardFilterSelectSx}
                            >
                                <MenuItem value="">All Sources</MenuItem>
                                <MenuItem value="Direct">Direct</MenuItem>
                                <MenuItem value="OTA">OTA/Expedia</MenuItem>
                                <MenuItem value="Corporate">Corporate/GDS</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={4} md={2}>
                        <FormControl size="small" fullWidth>
                            <Select
                                value={resFilters.roomTypeId}
                                displayEmpty
                                onChange={(e) => handleResFilterChange('roomTypeId', e.target.value)}
                                sx={standardFilterSelectSx}
                            >
                                <MenuItem value="">All Room Types</MenuItem>
                                {roomTypes.map((type) => (
                                    <MenuItem key={type.id || type._id} value={type.id || type._id}>
                                        {type.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                </Grid>
                <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end', gap: 1.5 }}>
                    {(resFilters.startDate !== '' || resFilters.endDate !== '' || resFilters.period !== 'daily' || resFilters.status !== '' || resFilters.bookingSource !== '' || resFilters.roomTypeId !== '') && (
                        <Button variant="outlined" startIcon={<ReplayIcon sx={{ fontSize: 16 }} />} sx={standardResetButtonSx} onClick={resetResFilters} disabled={isExporting}>Reset</Button>
                    )}
                    <Button 
                        variant="outlined" 
                        onClick={handleExportClick}
                        disabled={isExporting}
                        startIcon={<FileDownloadOutlinedIcon sx={{ fontSize: 18 }} />}
                        endIcon={<KeyboardArrowDownIcon />}
                        sx={standardSecondaryButtonSx}
                    >
                        {isExporting ? 'Exporting...' : 'Export Report'}
                    </Button>
                    <Menu
                        anchorEl={exportAnchorEl}
                        open={Boolean(exportAnchorEl)}
                        onClose={handleExportClose}
                    >
                        <MenuItem onClick={() => handleExport('csv')}>Export as CSV</MenuItem>
                        <MenuItem onClick={() => handleExport('excel')}>Export as Excel</MenuItem>
                        <MenuItem onClick={() => handleExport('pdf')}>Export as PDF</MenuItem>
                    </Menu>
                </Box>
            </Paper>

            {resSummary && !isLoadingRes && (
                <Grid container spacing={2} sx={{ mb: 3 }}>
                    <Grid item xs={6} md={3}>
                        <Paper sx={{ p: 2, textAlign: 'center', borderRadius: 2, bgcolor: 'rgba(0,0,0,0.02)' }}>
                            <Typography variant="h5" color="primary" fontWeight="bold">{resSummary.totalBookings || 0}</Typography>
                            <Typography variant="body2" color="text.secondary">Total Bookings</Typography>
                        </Paper>
                    </Grid>
                    <Grid item xs={6} md={3}>
                        <Paper sx={{ p: 2, textAlign: 'center', borderRadius: 2, bgcolor: 'rgba(0,0,0,0.02)' }}>
                            <Typography variant="h5" color="primary" fontWeight="bold">
                                {formatCurrency(resSummary.totalRevenue || 0)}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">Total Revenue</Typography>
                        </Paper>
                    </Grid>
                    <Grid item xs={6} md={3}>
                        <Paper sx={{ p: 2, textAlign: 'center', borderRadius: 2, bgcolor: 'rgba(0,0,0,0.02)' }}>
                            <Typography variant="h5" color="primary" fontWeight="bold">{resSummary.cancellationCount || 0}</Typography>
                            <Typography variant="body2" color="text.secondary">Cancellations</Typography>
                        </Paper>
                    </Grid>
                    <Grid item xs={6} md={3}>
                        <Paper sx={{ p: 2, textAlign: 'center', borderRadius: 2, bgcolor: 'rgba(0,0,0,0.02)' }}>
                            <Typography variant="h5" color="primary" fontWeight="bold">{Number(resSummary.cancellationRate || 0).toFixed(2)}%</Typography>
                            <Typography variant="body2" color="text.secondary">Cancel Rate</Typography>
                        </Paper>
                    </Grid>
                </Grid>
            )}

            <TableContainer component={Paper}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>Booking ID / Guest</TableCell>
                            <TableCell>Dates</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell>Source</TableCell>
                            <TableCell>Room Type</TableCell>
                            <TableCell align="right">Total Amount</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {isLoadingRes ? (
                            Array.from(new Array(5)).map((_, idx) => (
                                <TableRow key={idx}>
                                    <TableCell><Skeleton animation="wave" /></TableCell>
                                    <TableCell><Skeleton animation="wave" /></TableCell>
                                    <TableCell><Skeleton animation="wave" /></TableCell>
                                    <TableCell><Skeleton animation="wave" /></TableCell>
                                    <TableCell><Skeleton animation="wave" /></TableCell>
                                    <TableCell><Skeleton animation="wave" /></TableCell>
                                </TableRow>
                            ))
                        ) : (
                            resData.length > 0 ? (
                                resData.map((row, idx) => (
                                    <TableRow key={idx} hover>
                                        <TableCell>
                                            <Typography variant="body2" fontWeight="bold">{row.bookingId || row.id?.substring(0, 8) || '-'}</Typography>
                                            <Typography variant="caption" color="text.secondary">{row.guest?.name || '-'}</Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2">{row.checkIn ? new Date(row.checkIn).toLocaleDateString() : '-'}</Typography>
                                            <Typography variant="caption" color="text.secondary">to {row.checkOut ? new Date(row.checkOut).toLocaleDateString() : '-'}</Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Chip 
                                                label={row.status || 'Unknown'} 
                                                size="small" 
                                                color={(row.status || '').toLowerCase() === 'confirmed' ? 'primary' : ((row.status || '').toLowerCase() === 'cancelled' ? 'error' : 'default')} 
                                            />
                                        </TableCell>
                                        <TableCell>{row.bookingSource || '-'}</TableCell>
                                        <TableCell>
                                            {row.rooms && row.rooms.length > 0 
                                                ? row.rooms.map(r => r.room?.type || r.room?.roomNumber || 'Unknown').join(', ') 
                                                : 'Unassigned'}
                                        </TableCell>
                                        <TableCell align="right">
                                            {formatCurrency(row.totalAmount || 0)}
                                        </TableCell>
                                    </TableRow>
                                ))
                            ) : (
                                <TableRow>
                                    <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                                        <Typography color="text.secondary">No reservation records found for the selected criteria.</Typography>
                                    </TableCell>
                                </TableRow>
                            )
                        )}
                    </TableBody>
                </Table>
                <TablePagination
                    component="div"
                    count={resData.length >= resRowsPerPage ? -1 : resData.length}
                    page={resPage}
                    onPageChange={(e, newPage) => setResPage(newPage)}
                    rowsPerPage={resRowsPerPage}
                    onRowsPerPageChange={(e) => {
                        setResRowsPerPage(parseInt(e.target.value, 10));
                        setResPage(0);
                    }}
                    rowsPerPageOptions={[20, 50, 100]}
                />
            </TableContainer>
        </Box>
    );
}
